import { useCallback, useEffect, useMemo, useState } from 'react'

const onlyDigits = v => String(v || '').replace(/\D/g, '')
const pad2 = v => String(v || '').padStart(2, '0')

const clampNum = (n, min, max) => Math.max(min, Math.min(max, n))

// Parse 24h string like "13:30" into { hh12, mm, ap, raw }
const parse24 = value => {
	if (!value || typeof value !== 'string') {
		return { raw: '', hh12: '', mm: '', ap: 'AM' }
	}
	const [hStr, mStr] = value.split(':')
	let h24 = Number(hStr)
	let mm = Number(mStr)
	if (!Number.isFinite(h24) || !Number.isFinite(mm)) {
		return { raw: '', hh12: '', mm: '', ap: 'AM' }
	}
	mm = clampNum(mm, 0, 59)
	const ap = h24 >= 12 ? 'PM' : 'AM'
	let hh12 = h24 % 12
	if (hh12 === 0) hh12 = 12
	return {
		hh12: pad2(hh12),
		mm: pad2(mm),
		ap,
		raw: `${pad2(hh12)}${pad2(mm)}`,
	}
}

export const useTimeInput = ({ value, onChange }) => {
	const [state, setState] = useState(() => parse24(value))

	useEffect(() => {
		setState(parse24(value))
	}, [value])

	const emit = useCallback(
		next => {
			setState(next)

			const hh12 = Number(next.hh12)
			const mm = Number(next.mm)
			if (!Number.isFinite(hh12) || !Number.isFinite(mm)) {
				onChange?.('')
				return
			}

			const hClamped = clampNum(hh12 || 1, 1, 12)
			const mClamped = clampNum(mm, 0, 59)

			let h24 = hClamped % 12
			if (next.ap === 'PM') h24 += 12

			onChange?.(`${pad2(h24)}:${pad2(mClamped)}`)
		},
		[onChange],
	)

	const setRaw = useCallback(raw => {
		const d = onlyDigits(raw).slice(0, 4)
		let hh = ''
		let mm = ''
		if (d.length <= 2) {
			hh = d
		} else {
			hh = d.slice(0, 2)
			mm = d.slice(2)
		}
		setState(prev => ({ ...prev, raw: d, hh12: hh, mm }))
	}, [])

	const onBlur = useCallback(() => {
		const hhNorm = pad2(clampNum(Number(state.hh12 || '0') || 1, 1, 12))
		const mmNorm = pad2(clampNum(Number(state.mm || '0'), 0, 59))
		emit({
			...state,
			raw: `${hhNorm}${mmNorm}`,
			hh12: hhNorm,
			mm: mmNorm,
		})
	}, [emit, state])

	const toggleAP = useCallback(() => {
		emit({ ...state, ap: state.ap === 'AM' ? 'PM' : 'AM' })
	}, [emit, state])

	const setAP = useCallback(
		nextAP => {
			if (nextAP !== 'AM' && nextAP !== 'PM') return
			emit({ ...state, ap: nextAP })
		},
		[emit, state],
	)

	const display = useMemo(() => {
		const d = state.raw || ''
		if (d.length <= 2) return d
		return `${d.slice(0, 2)}:${d.slice(2)}`
	}, [state.raw])

	return {
		display,
		ap: state.ap,
		setRaw,
		onBlur,
		toggleAP,
		setAP,
	}
}
